/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { AllCommunityModule } from 'ag-grid-community'
import type { ColDef, GridReadyEvent } from 'ag-grid-community'
import { AgGridProvider, AgGridReact } from 'ag-grid-react'
import colors from '@sportninja/common/constants/appColors'
import { font, weightSemiBold } from 'src/components/css'
import { dataGridTheme } from './theme'
import { DataGridLoadingOverlay, DataGridNoRowsOverlay } from './overlays'
import { DATA_GRID_TOKENS as T } from './tokens'
import { useHorizontalScrollAffordance } from './useHorizontalScrollAffordance'
import type { DataGridProps } from './types'

/**
 * The Community feature set (MIT). Range selection, the fill handle and the rich
 * select editor are Enterprise and deliberately absent — nothing here needs a
 * licence key.
 */
const COMMUNITY_MODULES = [AllCommunityModule]

/**
 * Column behaviour every grid in the app gets for free. A caller's own
 * `defaultColDef` is shallow-merged OVER this, so opting a grid out of sorting
 * or resizing stays a one-line override rather than a re-declaration.
 *
 * `unSortIcon` is what makes a sortable column LOOK sortable. AG Grid shows an
 * arrow only once you have already sorted; every antd table in the app carries
 * the double caret from the start, and a column that gives no hint reads as
 * static. Columns that opt out of sorting still show nothing.
 */
const BASE_DEFAULT_COL_DEF: ColDef = {
  sortable: true,
  resizable: true,
  unSortIcon: true,
}

/**
 * `${font.body}` is what makes `fontFamily: 'inherit'` in the theme resolve to
 * the tenant font. `min-width: 0` is the load-bearing half of the containment
 * pair: without it a grid inside a flex column refuses to shrink below its
 * content width and pushes the PAGE sideways instead of scrolling inside itself.
 */
const wrapperStyle = css`
  ${font.body}
  width: 100%;
  min-width: 0;

  /*
   * Column headers match the app's antd tables (Statistics, Standings): body
   * size, sentence case, semi-bold, full-strength white. They used to be 11px
   * uppercase and tracked out, borrowed from the registration tables — two
   * header conventions in one product, and a grid that read as a widget
   * dropped into the page rather than one of its tables.
   */
  .ag-header-cell-text {
    ${weightSemiBold}
    font-size: ${T.fontSize}px;
    color: ${T.headerText};
  }

  /*
   * A GROUP header is a different job — it labels a band of columns rather than
   * naming a value — so it keeps the small-caps treatment and steps back in
   * colour. That contrast is what stops the two header rows reading as one.
   */
  .ag-header-group-text {
    font-size: 11px;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${T.groupHeaderText};
  }

  /*
   * The sort affordance, coloured like the antd tables': a muted double caret
   * while the column is unsorted, the tenant accent once it is. The theme's
   * accentColor does not reach these — Quartz paints sort icons in the header
   * text colour, which would make an active sort indistinguishable from an
   * inactive one.
   */
  .ag-sort-none-icon {
    color: ${T.sortIdle};
  }
  .ag-sort-ascending-icon,
  .ag-sort-descending-icon {
    color: ${T.accent};
  }

  .ag-cell {
    font-variant-numeric: tabular-nums;
  }

  /*
   * A grid wider than its container scrolls sideways, and the browser's default
   * overlay scrollbar is a near-invisible hairline against a dark surface — so
   * the columns past the fold read as MISSING rather than as scrolled away.
   * This is the only cue that there is more sheet to the right.
   */
  .ag-body-horizontal-scroll-viewport {
    scrollbar-width: thin;
    scrollbar-color: ${colors.SECONDARY_100} transparent;

    &::-webkit-scrollbar {
      height: 10px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 5px;
      background: ${colors.SECONDARY_100};
    }
    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.NEUTRAL_400};
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
`

/**
 * SN-8356 — the edge fade, and the positioning context it needs.
 *
 * Applied only when `horizontalScrollAffordance` is on, so no other grid gains a
 * stacking context it did not ask for.
 */
const affordanceWrapperStyle = css`
  position: relative;
`

/**
 * A SHADOW rather than a colour match. The rows alternate two surfaces
 * (`oddRowBackgroundColor`), so a fade ending in either one would be visibly
 * wrong over half the sheet; darkening toward the palette's deepest surface
 * reads correctly over both, over the header, and over the pinned totals band.
 *
 * `pointer-events: none` and `aria-hidden` are load-bearing: it sits over the
 * cells, and the MANAGE kebab, the checkboxes and the inline editors underneath
 * must all keep working. Absolutely positioned, so it can never contribute to
 * the grid's own `scrollWidth` and feed itself.
 */
const scrollFadeStyle = css`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 48px;
  pointer-events: none;
  z-index: 1;
  background: linear-gradient(
    to right,
    ${colors.SECONDARY_500}00,
    ${colors.SECONDARY_500}B3
  );
`

/**
 * Shared AG Grid (Community) wrapper.
 *
 * Owns exactly the concerns that must not diverge between grids — module
 * registration, the tenant-aware dark theme, typography, width containment and
 * localized overlays — and passes everything else straight through. Columns,
 * row types, editability and keyboard behaviour belong to the feature.
 *
 * @example
 * <DataGrid<BalanceGridRow>
 *   rowData={rows}
 *   columnDefs={columns}
 *   getRowId={({ data }) => data.key}
 * />
 */
const DataGrid = <TRow,>({
  defaultColDef,
  loading = false,
  className,
  'data-testid': testId,
  /*
   * Destructured BEFORE the rest spread, or AgGridReact receives it as an
   * unknown grid option and warns about it on every render.
   */
  horizontalScrollAffordance = false,
  autoHeightMinBodyHeight,
  onGridReady,
  ...gridOptions
}: DataGridProps<TRow>) => {
  'use memo'

  const { wrapperRef, captureApi, fade } = useHorizontalScrollAffordance(
    horizontalScrollAffordance
  )

  // Only derive a new theme when a grid overrides the minimum; every other grid
  // keeps the shared theme object.
  const theme =
    autoHeightMinBodyHeight === undefined
      ? dataGridTheme
      : dataGridTheme.withParams({ autoHeightMinBodyHeight })

  const mergedDefaultColDef: ColDef<TRow> = {
    ...(BASE_DEFAULT_COL_DEF as ColDef<TRow>),
    ...defaultColDef,
  }

  return (
    <div
      ref={wrapperRef}
      css={
        horizontalScrollAffordance
          ? [wrapperStyle, affordanceWrapperStyle]
          : wrapperStyle
      }
      className={className}
      data-testid={testId}
    >
      <AgGridProvider modules={COMMUNITY_MODULES}>
        <AgGridReact<TRow>
          theme={theme}
          defaultColDef={mergedDefaultColDef}
          loading={loading}
          loadingOverlayComponent={DataGridLoadingOverlay}
          noRowsOverlayComponent={DataGridNoRowsOverlay}
          onGridReady={(event: GridReadyEvent<TRow>) => {
            // COMPOSED, not replaced — the feature's own handler still runs.
            captureApi(event.api)
            onGridReady?.(event)
          }}
          {...gridOptions}
        />
      </AgGridProvider>
      {fade.visible ? (
        <div
          aria-hidden='true'
          data-testid='data-grid-scroll-fade'
          css={scrollFadeStyle}
          style={{ right: fade.inset }}
        />
      ) : null}
    </div>
  )
}

export type { DataGridProps }
export default DataGrid
